import React, { Suspense, useEffect, useRef, useState } from 'react';

import { useMediaQuery } from '@mui/material';
import { Theme, useTheme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import clsx from 'clsx';
import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import { hashHistory } from 'react-router';

import { Button } from '../../components/atoms/Button';
import { Snackbar } from '../../components/atoms/Snackbar';
import { Typography } from '../../components/atoms/Typography';
import { BottomBar } from '../../components/layouts/BottomBar';
import { Hidden } from '../../components/layouts/Hidden';
import { LoadingSpinner } from '../../components/layouts/LoadingSpinner';
import { Page } from '../../components/layouts/Page';
import { OrderSummaryLine } from '../../components/molecules/OrderSummaryLine';
import { ReceiptButton } from '../../components/molecules/ReceiptButton';
import { ContactRestaurantInfoBox } from '../../components/partials/ContactRestaurantInfoBox';
import { OrderDescriptionList } from '../../components/partials/OrderDescriptionList';
import { OrderStatus } from '../../components/partials/OrderStatus';
import { PageHeader } from '../../components/partials/PageHeader';
import ProfilePanel from '../../components/partials/ProfilePanel';
import { analyticsService } from '../../core/services/analyticsService';
import { flagService } from '../../core/services/flagService';
import { FulfilmentStatus } from '../../core/state/order/order.types';
import { getFulfilmentStatus } from '../../core/state/order/orderMiddleware';
import { useInterval } from '../../utils/hooks/useInterval';
import { usePrevious } from '../../utils/hooks/usePrevious';
import {
  getOrderCollectionOrDeliveryTime,
  getOrderStatusPollDelay,
  getOrderStatusViewedEventData,
  getTaxLabel,
} from '../../utils/orderUtils';
import { storeListUtils } from '../../utils/storeListUtils';
import * as Api from '../../weborder/Api';
import { DispatchProps, StateProps } from '../../weborder/component/OrderView';
import { RouteParams } from '../../weborder/Routes';
import { orderApiServices, storageServices } from '../../weborder/services';
import { getWLID } from '../../weborder/Utilities';
import { LoyaltyProgressPopout } from './components/LoyaltyProgressPopout';
import { OrderCompleteError } from './partials/OrderCompleteError';
import { OrderedItemsList } from './partials/OrderedItemsList';
import { OrderStatusBar } from './partials/OrderStatusBar';
import {
  getCampaignOrRewardedVoucher,
  OrderCompleteUtils,
} from './utils/OrderCompleteUtils';

const useStyles = makeStyles((theme: Theme) => ({
  address: {
    marginBottom: theme.spacing(2),
    paddingLeft: 0,
    paddingRight: 0,
  },
  cancelButton: {
    padding: 0,
    margin: theme.spacing(2, 0, 2, 0),
    '&.MuiButton-outlined': {
      color: theme.palette.common.black,

      '& span': {
        color: theme.palette.common.black,
      },
      '& .fd-text': {
        color: theme.palette.common.black,
        fontSize: theme.typography.pxToRem(14),
        fontWeight: theme.typography.fontWeightBold,
      },
      '&:hover': {
        textDecoration: `underline ${theme.palette.common.black}`,
      },
    },
  },
  divider: {
    margin: theme.spacing(5, 0, 2),
  },
  fullWidth: {
    padding: theme.spacing(0, 2),
    width: '100%',
  },
  listItem: {
    alignItems: 'flex-start',
    paddingLeft: 0,
    paddingRight: 0,
    width: '100%',
  },
  orderedInfo: {
    '& li': {
      borderBottom: `1px solid ${theme.palette.grey[400]}`,
      '&:last-of-type': {
        border: 'unset',
      },
    },

    '&.orderedConcessionItemsSummary': {
      '& li:first-child': {
        border: 'unset',
      },
      '& ul': {
        '& li:last-of-type': {
          border: 'unset',
        },
      },
    },
  },
  total: {
    marginBottom: theme.spacing(11),
  },
  totalListItem: {
    fontWeight: 'bold',
  },
  price: {
    textAlign: 'right',
  },
  buttonsContainer: {
    display: 'flex',
    flexFlow: 'column nowrap',

    '& button, & a': {
      '&:first-child': {
        marginBottom: theme.spacing(1),
        [theme.breakpoints.up('md')]: {
          marginBottom: 0,
        },
      },
    },
  },
  tableOrderButtons: {
    justifyContent: 'flex-end',
  },
  [theme.breakpoints.up('md')]: {
    fullWidth: {
      padding: 0,
    },
    total: {
      marginBottom: theme.spacing(2.5),
    },
    buttonsContainer: {
      flexFlow: 'row nowrap',

      '& button, & a': {
        marginTop: theme.spacing(1.25),
        marginRight: theme.spacing(2),
      },
    },
    tableOrderButtons: {
      flexDirection: 'row-reverse',
    },
  },
  [theme.breakpoints.up('sm')]: {
    fullWidth: {
      padding: theme.spacing(0, 3),
    },
  },
  [theme.breakpoints.only('sm')]: {
    cancelButton: {
      margin: theme.spacing(2, 0, 2, 3),
    },
  },
  [theme.breakpoints.down('sm')]: {
    cancelButton: {
      margin: theme.spacing(2, 0, 2, 2),
    },
    divider: {
      borderTop: `1px solid ${theme.palette.grey[600]}`,
      padding: `${theme.spacing(1)} !important`,
    },
  },
}));

export type OrderCompleteProps = StateProps & DispatchProps & RouteParams;

const ORDER_CANCELLED_STATES = [
  Api.OrderState.Cancelled,
  Api.OrderState.RejectedByRestaurant,
  Api.OrderState.RejectedByFlipdish,
  Api.OrderState.RejectedAutomatically,
  Api.OrderState.RejectedAfterBeingAccepted,
];

export function OrderComplete(props: OrderCompleteProps): JSX.Element {
  const {
    appConfig,
    basketSnapshot,
    cancelCheckoutFlow,
    cancelOrderErrorMessage,
    cancelTimeout,
    ClearBasket: clearBasket,
    collectionRestaurants,
    concessionStores,
    continueCheckoutFlow,
    customer,
    deliveryRestaurants,
    getCampaignProgress,
    getOrderHistory,
    getRestaurantData,
    isFirstMount,
    loyaltyCampaigns,
    params,
    setDefaultPayment,
    translate,
  } = props;

  const concessionStoreId = Number(params.concessionStoreId);
  const orderId = Number(params.orderId);

  const { getBasketSummary, getCheckoutUrl, getFormatAddress, getMenuLink } =
    OrderCompleteUtils;

  const classes = useStyles();

  const initialCountdownSeconds = 15;
  const [countDown, setCountDown] = useState(initialCountdownSeconds);
  const [error, setError] = useState<Error>(undefined);
  const [loading, setLoading] = useState(true);
  const [order, setOrder] = useState<
    Api.PreviousOrderDm & { isCancelled: boolean }
  >();
  const [orderStatus, setOrderStatus] = useState<FulfilmentStatus>();
  const [showPopout, setShowPopout] = useState<boolean>(true);
  const [orderTime, setOrderTime] = useState<string>(undefined);

  // if the flag is active and an error response has returned from the backend.
  const redirectToCheckoutIsActive = order && order?.PaymentProcessingResponse;
  const storeCampaigns = loyaltyCampaigns
    ? loyaltyCampaigns[order?.PhysicalRestaurantId]?.data || []
    : [];
  const meta = storageServices.getSubmittedOrderMeta(orderId);
  const menuId = (meta && meta.menuId) || undefined;

  const timer = useRef<NodeJS.Timeout>();
  const statusTimer = useRef<NodeJS.Timeout>();
  const isOrderStatusEnabled = flagService.isFlagOn('orderStatus');
  const isLoyaltyEnabled = flagService.isFlagOn('loyaltyStatus');
  const theme = useTheme();
  const isMobileScreen = useMediaQuery(theme?.breakpoints.down('sm'));
  const prevOrderStatus = usePrevious(orderStatus);

  const loadOrder = (): Promise<void> =>
    orderApiServices
      .get({ orderId })
      .then((orderData) => {
        if (orderData) {
          if (
            orderData.DeliveryType === Api.DeliveryType.Collection &&
            isOrderStatusEnabled &&
            collectionRestaurants &&
            Object.keys(collectionRestaurants).length === 0
          ) {
            getRestaurantData(
              orderData.VirtualRestaurantId,
              'collection',
              orderData.PhysicalRestaurantId,
            );
          } else if (
            orderData.DeliveryType === Api.DeliveryType.Delivery &&
            isOrderStatusEnabled &&
            deliveryRestaurants &&
            Object.keys(deliveryRestaurants).length === 0
          ) {
            getRestaurantData(
              orderData.VirtualRestaurantId,
              'delivery',
              orderData.DeliveryLocationId,
            );
          }

          const orderCancelled = ORDER_CANCELLED_STATES.includes(
            orderData.OrderState,
          );

          if (!cancelTimeout && !orderCancelled) {
            const paymentId = meta && meta.paymentId;

            if (paymentId) {
              setDefaultPayment({ paymentId });
            }

            clearBasket({ menuId });
          }

          setOrder({ ...orderData, isCancelled: orderCancelled });
        } else {
          throw new Error('no order data found');
        }
      })
      .catch((orderError) => {
        setOrder(undefined);
        throw orderError;
      });

  const loadOrderStatus = (): Promise<void> =>
    getFulfilmentStatus(orderId)
      .then((fulfilmentStatus) => {
        setOrderStatus(fulfilmentStatus);
        trackOrderStatus(fulfilmentStatus);
      })
      .catch(() => setOrderStatus(null));

  const handleVisibilityChange = (): void => {
    if (document.visibilityState === 'visible') {
      statusTimer.current = setTimeout(() => {
        loadOrderStatus();
      }, 500);
    }
  };

  useEffect(() => {
    const time = getOrderCollectionOrDeliveryTime(
      order,
      orderStatus,
      collectionRestaurants,
      deliveryRestaurants,
      translate,
    );
    setOrderTime(time);
  }, [collectionRestaurants, deliveryRestaurants, order, orderStatus]);

  useEffect(() => {
    if (cancelTimeout !== 0) {
      timer.current = setInterval(() => {
        setCountDown((prevCount) => prevCount - 1);
      }, 1000);

      if (!countDown) {
        clearInterval(timer.current);
      }
    } else {
      setCountDown(0);
    }
    return () => {
      clearInterval(timer.current);
    };
  }, [cancelTimeout, countDown]);

  useEffect(
    () => () => {
      if (!isCancelFlag.current) {
        continueCheckoutFlow();
      }
    },
    [],
  );

  useEffect(() => {
    if (redirectToCheckoutIsActive) {
      redirectToCheckoutOnPaymentError();
    }
  }, [order]);

  useEffect(() => {
    if (isLoyaltyEnabled && orderStatus && prevOrderStatus) {
      // get latest campaign progress if order status has changed from pending and is not rejected
      const orderConfirmed =
        prevOrderStatus.StatusId === 'pending' &&
        orderStatus.StatusId !== 'pending' &&
        !orderStatus.IsError;
      if (orderConfirmed) {
        getCampaignProgress(getWLID(), order.PhysicalRestaurantId);
      }
    }
  }, [isLoyaltyEnabled, orderStatus?.StatusId, prevOrderStatus?.StatusId]);

  useEffect(() => {
    if (isLoyaltyEnabled && order) {
      getCampaignProgress(getWLID(), order.PhysicalRestaurantId);
    }
  }, [isLoyaltyEnabled, order]);

  useEffect(() => {
    if (orderId) {
      setLoading(true);
      if (isOrderStatusEnabled) {
        document.addEventListener('visibilitychange', handleVisibilityChange);
        loadOrder()
          .then(() => loadOrderStatus())
          .then(() => setError(undefined))
          .catch(() => setError(new Error('Order_loading_error')))
          .finally(() => setLoading(false));
      } else {
        loadOrder()
          .then(() => setError(undefined))
          .catch(() => setError(new Error('Order_loading_error')))
          .finally(() => setLoading(false));
      }
    }
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (statusTimer.current) {
        clearTimeout(statusTimer.current);
      }
    };
  }, [orderId]);

  useEffect(() => {
    if (!countDown) {
      clearBasket({ menuId });
    }
  }, [countDown]);

  useEffect(() => {
    const trackPlaceOrderSuccessful = (): void => {
      const eventData = {
        amp_label: 'Place Order Successful',
        amp_totalPricePaidInEuro: order?.TotalAmount,
      };
      analyticsService.track(eventData);
    };

    const track3dsOrder = (): void => {
      const eventData = {
        amp_label:
          order.OrderState !== Api.OrderState.Cancelled
            ? '3DS Success'
            : '3DS Failed',
      };

      analyticsService.track(eventData);
    };

    if (basketSnapshot && order) {
      trackPlaceOrderSuccessful();
      // update order history page when new order is placed
      getOrderHistory();
    }

    const perfEntries = performance.getEntriesByType(
      'navigation',
    ) as PerformanceNavigationTiming[];
    const isNavigation =
      perfEntries.length && perfEntries[0].type === 'navigate';

    if (order?.RedirectUrl && isNavigation && isFirstMount) {
      track3dsOrder();
    }
  }, [order?.OrderId, basketSnapshot]);

  const trackOrderCancelled = (): void => {
    const timeElapsedTillOrderCanceled = initialCountdownSeconds - countDown;
    const eventData = {
      amp_cancelledAfterSeconds: timeElapsedTillOrderCanceled,
      amp_label: 'Order Cancelled',
    };
    analyticsService.track(eventData);
  };

  const trackOrderStatus = (fulfilmentStatus: FulfilmentStatus): void => {
    const eventData = getOrderStatusViewedEventData(fulfilmentStatus, orderId);
    analyticsService.track(eventData);
  };

  const retryLoadReceiptDelay = 10000;
  const refetchOrderStatusDelay = isOrderStatusEnabled
    ? getOrderStatusPollDelay(orderStatus)
    : null;
  const hasReceiptCode = order && order.ReceiptCode;
  const loadReceipt = (): void => {
    const isCancelled = order.OrderState === Api.OrderState.Cancelled;
    if (!isCancelled && !hasReceiptCode) {
      loadOrder().catch(() => setError(new Error('Order_loading_error')));
    }
  };

  useInterval(loadReceipt, !hasReceiptCode ? retryLoadReceiptDelay : null);
  useInterval(loadOrderStatus, refetchOrderStatusDelay);

  const basketSummary: any = getBasketSummary(basketSnapshot);
  const restaurantPhoneNumber = storeListUtils.getRestaurantPhoneNumber(
    order?.PhysicalRestaurantId,
    collectionRestaurants,
  );

  const isCancelFlag = useRef(false);
  const redirectToCheckoutOnPaymentError = (): void => {
    const checkoutURL = getCheckoutUrl(order, concessionStoreId);
    window.location.href = checkoutURL;
  };

  const redirectToMenu = (): void => {
    const menuUrl = getMenuLink(order, concessionStoreId);
    hashHistory.push(menuUrl);
  };

  const handleCancelOrder = (): void => {
    isCancelFlag.current = true;
    cancelCheckoutFlow();
    trackOrderCancelled();
    if (isOrderStatusEnabled) {
      loadOrderStatus();
    }
  };

  if (error) {
    return <OrderCompleteError error={error} translate={translate} />;
  }

  if (appConfig.status !== 'success' || loading || redirectToCheckoutIsActive) {
    return <LoadingSpinner />;
  }

  const currency = order?.Currency || Api.Currency.EUR;
  const languageCode = basketSummary?.initialLang;

  let customerName = null;
  if (customer.status === 'success') {
    customerName = customer.data.CustomerName;
  }

  const getOrderRestaurantAddress = (): string => {
    const orderRestaurant = _get(
      collectionRestaurants,
      `[${order.PhysicalRestaurantId.toString()}]`,
    );
    if (!orderRestaurant)
      return basketSummary?.physicalRestaurantAddress
        ? getFormatAddress(basketSummary.physicalRestaurantAddress)
        : '';

    const orderRestaurantAddress = _get(
      orderRestaurant,
      'data.PhysicalRestaurantAddress',
    );
    const address =
      basketSummary?.physicalRestaurantAddress ||
      getFormatAddress(orderRestaurantAddress);

    return address || '';
  };

  const getServiceChargeInfo = () => {
    let isApplied = false;
    let amount = 0;
    let showFee = true;

    if (basketSummary) {
      const {
        amountExcludingProcessingFee,
        displayAsOneWithProcessingFee,
        isEnabled,
      } = basketSummary.serviceCharge;

      const { excludeServiceCharge, processingFee } = basketSummary;

      isApplied = isEnabled && !excludeServiceCharge;

      amount = displayAsOneWithProcessingFee
        ? amountExcludingProcessingFee + processingFee
        : amountExcludingProcessingFee;

      showFee = !displayAsOneWithProcessingFee && processingFee > 0;
    }

    const showCharge = isApplied && amount > 0;

    return {
      serviceChargeAmount: amount,
      showProcessingFee: showFee,
      showServiceCharge: showCharge,
    };
  };

  const isTableService =
    order?.PickupLocationType === Api.PickupLocationType.TableService;
  const { serviceChargeAmount, showProcessingFee, showServiceCharge } =
    getServiceChargeInfo();

  const backButtonVariant =
    isTableService || order?.isCancelled ? 'primary' : 'secondary';

  const receiptButtonVariant = isTableService ? 'secondary' : 'primary';
  const receiptButtonStatus = hasReceiptCode ? 'default' : 'loading';

  const buttonStyles = clsx(
    classes.buttonsContainer,
    (isTableService || order?.isCancelled) && classes.tableOrderButtons,
  );

  const orderCompleteButtons = (
    <div className={buttonStyles}>
      {order && !order.isCancelled && (
        <ReceiptButton
          receiptButtonStatus={receiptButtonStatus}
          receiptCode={order.ReceiptCode}
          variant={receiptButtonVariant}
          translate={translate}
        />
      )}

      <Button onClick={redirectToMenu} variant={backButtonVariant}>
        {isTableService
          ? translate('Checkout_orderMoreNow')
          : translate('Back_to_menu')}
      </Button>
    </div>
  );

  const renderPopout = (): JSX.Element => {
    const isPending = orderStatus?.StatusId === 'pending';
    const pointerVariant = isMobileScreen ? 'right' : 'center';
    if (!isLoyaltyEnabled || storeCampaigns.length === 0) {
      return null;
    }
    const activeCampaign = getCampaignOrRewardedVoucher(
      storeCampaigns,
      isPending,
    );
    const requiredConditions = activeCampaign.RequiredConditions
      ? activeCampaign.RequiredConditions[0]
      : undefined;
    return (
      <LoyaltyProgressPopout
        conditions={requiredConditions}
        currency={currency}
        isPending={isPending}
        languageCode={languageCode}
        onClosePopout={(): void => setShowPopout(false)}
        pointerVariant={pointerVariant}
        reward={activeCampaign.Rewards?.[0]}
        showPopout={showPopout}
        translate={translate}
        voucher={activeCampaign.Voucher}
        voucherDescription={activeCampaign.Description}
      />
    );
  };

  /* TODO: create a partial for order details that can be used here and on order history details page
   and move this logic there */
  const renderOrderDetails = () => {
    const orderDetails = {
      address:
        order.DeliveryType === Api.DeliveryType.Delivery
          ? order.DeliveryLocationAddressString
          : `${order.RestaurantName} ${getOrderRestaurantAddress()}`,
      tax: basketSummary?.displayTax
        ? `${translate('Includes_tax_of', {
            tax: ` ${basketSummary.processingFeeTaxRate}%`,
          })}`
        : null,
      time: orderTime,
    };

    return (
      <>
        {isOrderStatusEnabled && countDown !== 0 && (
          <Button
            className={classes.cancelButton}
            data-fd="order-cancel-button"
            onClick={handleCancelOrder}
            variant="tertiary"
          >
            <Typography>
              {`${translate('OrderProcessing_CancelOrder')} ${countDown}`}
            </Typography>
          </Button>
        )}
        {!isOrderStatusEnabled && (
          <OrderStatusBar
            cancelTimeout={cancelTimeout}
            countDown={countDown}
            handleCancelOrder={handleCancelOrder}
            isCancelled={order?.isCancelled}
            translate={translate}
          />
        )}
        {isOrderStatusEnabled && (
          <OrderStatus orderStatus={orderStatus} translate={translate} />
        )}
        {cancelOrderErrorMessage && (
          <Snackbar message={cancelOrderErrorMessage} variant="error" />
        )}

        <OrderDescriptionList
          address={orderDetails.address}
          customerName={customerName}
          deliveryType={order.DeliveryType}
          hasOrderTime={!!orderDetails.time}
          isCancelled={order.isCancelled}
          isError={orderStatus?.IsError}
          orderId={order.OrderId}
          restaurantName={order.RestaurantName}
          timeOfOrder={orderDetails.time}
          translate={translate}
        />

        {basketSummary && (
          <ul className={orderedItemsSummary}>
            <OrderedItemsList
              basketSummary={basketSummary}
              concessionStores={concessionStores}
              currency={currency}
              languageCode={languageCode}
              menuId={menuId}
              translate={translate}
            />

            {showProcessingFee && (
              <li>
                <OrderSummaryLine
                  className={classes.listItem}
                  currency={currency}
                  label={`${translate('Online_ordering_fee')}`}
                  languageCode={languageCode}
                  subLabel={orderDetails.tax}
                  translate={translate}
                  value={basketSummary.processingFee}
                />
              </li>
            )}

            {showServiceCharge && (
              <li>
                <OrderSummaryLine
                  className={classes.listItem}
                  currency={currency}
                  label={`${translate('Checkout_Service_Charge_Label')}`}
                  languageCode={languageCode}
                  translate={translate}
                  value={serviceChargeAmount}
                />
              </li>
            )}

            {basketSummary.deliveryFee > 0 && (
              <li>
                <OrderSummaryLine
                  className={classes.listItem}
                  currency={currency}
                  label={`${translate('Delivery_fee')}`}
                  languageCode={languageCode}
                  translate={translate}
                  value={basketSummary.deliveryFee}
                />
              </li>
            )}

            {basketSummary.taxes > 0 && (
              <li>
                <OrderSummaryLine
                  className={classes.listItem}
                  currency={currency}
                  dataFd="order-complete-tax"
                  label={getTaxLabel(appConfig.data, translate)}
                  languageCode={languageCode}
                  translate={translate}
                  value={basketSummary.taxes}
                />
              </li>
            )}

            {order.TipAmount > 0 && basketSummary.percentageTip > 0 && (
              <li>
                <OrderSummaryLine
                  className={classes.listItem}
                  currency={currency}
                  dataFd="order-complete-tip"
                  label={`${translate('Tip')} ${basketSummary.percentageTip}%`}
                  languageCode={languageCode}
                  translate={translate}
                  value={order.TipAmount}
                />
              </li>
            )}

            {!_isEmpty(basketSummary.voucher.voucherCode.trim()) && (
              <li>
                <OrderSummaryLine
                  className={classes.listItem}
                  currency={currency}
                  label={basketSummary.voucher.voucherCode}
                  subLabel={basketSummary.voucher.voucherDescription}
                  languageCode={languageCode}
                  translate={translate}
                  value={basketSummary.voucher.voucherAmount}
                />
              </li>
            )}

            <li className={clsx(!isOrderStatusEnabled && classes.total)}>
              <OrderSummaryLine
                bigPrice
                className={clsx(classes.listItem, classes.totalListItem)}
                currency={currency}
                label={`${translate('Total')}`}
                subLabel={basketSummary.paymentType}
                languageCode={languageCode}
                translate={translate}
                value={order.TotalAmount}
              />
            </li>
          </ul>
        )}
        {restaurantPhoneNumber && isOrderStatusEnabled && (
          <ContactRestaurantInfoBox
            restaurantPhoneNumber={restaurantPhoneNumber}
            translate={translate}
          />
        )}
      </>
    );
  };

  const orderedItemsSummary = clsx(
    classes.fullWidth,
    classes.orderedInfo,
    concessionStores?.length > 0 && 'orderedConcessionItemsSummary',
  );

  return (
    <Page pageName="order-complete">
      <PageHeader showProfileMenu popout={renderPopout()} />
      <article className="fd-content__main">
        {order && renderOrderDetails()}
        <Hidden mdDown>{orderCompleteButtons}</Hidden>
      </article>
      <Hidden smDown>
        <section className="fd-content__side">
          <Suspense fallback={<div />}>
            <ProfilePanel
              params={{ vrid: order?.VirtualRestaurantId.toString() }}
            />
            {renderPopout()}
          </Suspense>
        </section>
      </Hidden>
      <Hidden mdUp>
        <BottomBar fixedOnly={['xs', 'sm']}>{orderCompleteButtons}</BottomBar>
      </Hidden>
    </Page>
  );
}
